import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import { PageHeader } from '../../components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Spinner } from '../../components/ui/Spinner';
import { BookOpen } from 'lucide-react';
import { useStudentCourses } from '../../modules/learning/hooks/useStudentCourses';
import type { CourseOut } from '../../modules/learning/types/catalog';

/** Group courses by grade. Grades with no courses are omitted. */
function groupByGrade(courses: CourseOut[]): Map<number, CourseOut[]> {
  const map = new Map<number, CourseOut[]>();
  for (const c of courses) {
    const g = c.grade ?? 0;
    if (!map.has(g)) map.set(g, []);
    map.get(g)!.push(c);
  }
  const sorted = new Map<number, CourseOut[]>();
  [...map.entries()].sort((a, b) => a[0] - b[0]).forEach(([k, v]) => sorted.set(k, v));
  return sorted;
}

export const StudentCourses: React.FC = () => {
  const navigate = useNavigate();
  const { courses, loading, error } = useStudentCourses(undefined);

  const showTiles = useMemo(() => {
    const grouped =
      courses.length > 0 ? groupByGrade(courses) : new Map<number, CourseOut[]>([[2, []]]);
    return [...grouped.entries()];
  }, [courses]);

  return (
    <Layout>
      <PageHeader
        title="Courses"
        subtitle="Select a grade to see parts"
      />

      {loading && (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4 text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {!loading && !error && showTiles.length === 0 && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-8 text-center text-gray-600 dark:text-gray-400">
          No grades available.
        </div>
      )}

      {!loading && !error && showTiles.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {showTiles.map(([grade, list]) => (
            <Card
              key={grade}
              className="cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all duration-200 border-2 border-gray-200 dark:border-gray-700 hover:border-primary-500 dark:hover:border-primary-500"
              onClick={() => navigate(`/student/grade/${grade}/parts`)}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-xl bg-primary-100 dark:bg-primary-900/50">
                    <BookOpen className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                  </div>
                  <CardTitle className="text-lg font-semibold text-gray-900 dark:text-white">
                    வகுப்பு {grade}
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {list.length} பகுதிகள்
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </Layout>
  );
};

